import { Alert, Button, ErrorText, Link, Pending } from '@cluey/cluey-components';
import update from 'immutability-helper';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { resetPassword } from '../../../../../actions/hub/hubAuthPagesActions';
import type { InitialState } from '../../../../../reducers/initialState';
import { extractEmailAndTempPassword } from '../../../../../util/helper/resetPassword';
import { PATH_HUB_RESET_PASSWORD_REQUEST } from '../../../../../util/pagePath';
import { posthog } from '../../../../../util/posthog';
import { validatePassword } from '../../../../../util/validation/password';
import { Auth2Layout } from '../../common';
import { InlineLoader } from '../../common';
import { useRedirectIfAuthenticated } from '../../common';
import { KeepMeLoggedIn } from './KeepMeLoggedIn';
import { PasswordField } from './PasswordField';

const translateErrorMessage = (errorMessage: string) => {
  if (errorMessage.includes('User does not exist')) {
    return 'There was a problem with your email. Please contact support.';
  }
  return errorMessage;
};

interface FormField {
  [key: string]: {
    error: string;
    valid: boolean;
    value: string;
    textVisible: boolean;
    validator(firstParam: string, secondParam?: string);
  };
}
function ResetPassword() {
  const dispatch = useDispatch();
  const loading = useSelector((state: InitialState) => state.ui.apiState.hubAuthPage.isLoading);
  const submitError = useSelector((state: InitialState) => state.ui.apiState.hubAuthPage.errorMsg);
  const submitErrorMessage = submitError ? translateErrorMessage(submitError) : '';
  const queryParams = new URLSearchParams(useLocation().search);
  const { token, email, tempPassword } = extractEmailAndTempPassword(queryParams);
  useRedirectIfAuthenticated();

  const passwordRequirements = [
    'At least 8 characters',
    'At least 1 uppercase letter',
    'At least 1 lowercase letter',
    'At least 1 number',
  ];

  const [formData, setFormData] = useState<FormField>({
    newPassword: {
      error: '',
      valid: true,
      value: '',
      textVisible: false,
      validator: (value: string) => {
        return !!validatePassword(value);
      },
    },
    newPasswordConfirmation: {
      error: '',
      valid: true,
      value: '',
      textVisible: false,
      validator: (password, passwordConfirmation) => {
        return password === passwordConfirmation && password.length > 0;
      },
    },
  });

  const onChange = (value: string, event: React.ChangeEvent) => {
    setFormData((current) => {
      const valid = current[event.target.id].validator(value, formData.newPassword.value);
      const errorMessage =
        event.target.id === 'newPassword' ? 'Password does not meet the requirements.' : "The passwords don't match.";
      return update(current, {
        [event.target.id]: {
          $merge: {
            value,
            valid,
            error: valid ? '' : errorMessage,
          },
        },
      });
    });
  };

  const onEyeClick = (target: 'newPassword' | 'newPasswordConfirmation') => {
    setFormData((current) => {
      return update(current, {
        [target]: {
          textVisible: {
            $set: !current[target].textVisible,
          },
        },
      });
    });
  };

  const onResetButtonClick = () => {
    posthog.capture('click_reset-password_button');
    const validationResult: FormField = Object.entries(formData).reduce((acc, fieldItem) => {
      const [field, fieldData] = fieldItem;
      const valid = fieldData.validator(fieldData.value, formData.newPassword.value);
      const errorMessage =
        field === 'newPassword' ? 'Password does not meet the requirements.' : "The passwords don't match.";
      return update(acc, {
        $merge: {
          [field]: update(fieldData, {
            $merge: {
              valid,
              error: valid ? '' : errorMessage,
            },
          }),
        },
      });
    }, {});

    setFormData(validationResult);
    if (Object.values(validationResult).every((field) => field.valid)) {
      dispatch(
        resetPassword({
          email,
          tempPassword,
          newPassword: formData.newPassword.value,
        })
      );
    }
  };

  return (
    <Auth2Layout title="Set your new password">
      <Pending loading={loading} loader={<InlineLoader />}>
        {!token && (
          <Alert type="error" body={null}>
            Invalid reset password link - please use the link sent to your email.
          </Alert>
        )}
        <div className="text-sm text-grey-6">
          <p className="font-bold">Create your new password below. It should include:</p>
          <ul className="mt-4 list-disc pl-2">
            {passwordRequirements.map((requirement) => (
              <li key={requirement} className="flex items-center gap-2">
                <div className="h-1 w-1 rounded-full bg-black opacity-70"></div>
                <div>{requirement}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex w-full flex-col gap-1">
            <PasswordField
              id="newPassword"
              label="New password"
              onChange={onChange}
              data={formData.newPassword}
              onEyeClick={onEyeClick}
            />

            <PasswordField
              id="newPasswordConfirmation"
              label="Confirm new password"
              onChange={onChange}
              data={formData.newPasswordConfirmation}
              onEyeClick={onEyeClick}
            />
          </div>
        </div>
        <div className="my-2">
          <KeepMeLoggedIn />
        </div>
        {!token && (
          <Link to={PATH_HUB_RESET_PASSWORD_REQUEST} className="link">
            Don't have the email? Request a new one
          </Link>
        )}
        {submitErrorMessage && <ErrorText>{submitErrorMessage}</ErrorText>}
        <Button disabled={!token} className="my-5" onClick={onResetButtonClick} size="lg">
          Reset password
        </Button>
      </Pending>
    </Auth2Layout>
  );
}

export default ResetPassword;
